/**
 * Executive client trending under `GET/POST /api/v2/clients/{person_uuid}/trending/…`.
 * Mirrors team trending payloads; replace with generated `sdk.gen` + `react-query.gen`
 * once OpenAPI includes these operations.
 */
import { queryOptions } from '@tanstack/react-query';

import { client as _heyApiClient } from 'client/client.gen';
import type { Options } from 'client/sdk.gen';
import type {
  GetTeamTrendingErrors,
  GetTeamTrendingRecommendationActivityErrors,
  GetTeamTrendingRecommendationActivityResponses,
  GetTeamTrendingRecommendationErrors,
  GetTeamTrendingResponses,
  GetTeamTrendingRecommendationResponses,
  PostTeamTrendingRecommendationCommentsData,
  PostTeamTrendingRecommendationCommentsErrors,
  PostTeamTrendingRecommendationCommentsResponses,
} from 'client/types.gen';

export type GetExecutiveClientTrendingData = {
  body?: never;
  path: {
    person_uuid: string;
  };
  query?: {
    search_id?: string | null;
  };
  url: '/api/v2/clients/{person_uuid}/trending';
};

export type GetExecutiveClientTrendingRecommendationData = {
  body?: never;
  path: {
    person_uuid: string;
    recommendation_uuid: string;
  };
  query?: never;
  url: '/api/v2/clients/{person_uuid}/trending/{recommendation_uuid}';
};

export type GetExecutiveClientTrendingRecommendationActivityData = {
  body?: never;
  path: {
    person_uuid: string;
    recommendation_uuid: string;
  };
  query?: {
    team_uuids?: Array<string> | null;
    page?: number;
    size?: number;
  };
  url: '/api/v2/clients/{person_uuid}/trending/{recommendation_uuid}/activity';
};

export type PostExecutiveClientTrendingRecommendationCommentsData = {
  body: PostTeamTrendingRecommendationCommentsData['body'];
  path: {
    person_uuid: string;
    recommendation_uuid: string;
  };
  query?: never;
  url: '/api/v2/clients/{person_uuid}/trending/{recommendation_uuid}/comments';
};

export const getExecutiveClientTrending = <
  ThrowOnError extends boolean = false,
>(
  options: Options<GetExecutiveClientTrendingData, ThrowOnError>,
) => {
  return _heyApiClient.get<
    GetTeamTrendingResponses,
    GetTeamTrendingErrors,
    ThrowOnError
  >({
    responseType: 'json',
    security: [
      {
        scheme: 'bearer',
        type: 'http',
      },
    ],
    url: '/api/v2/clients/{person_uuid}/trending',
    ...options,
  });
};

export const getExecutiveClientTrendingRecommendation = <
  ThrowOnError extends boolean = false,
>(
  options: Options<GetExecutiveClientTrendingRecommendationData, ThrowOnError>,
) => {
  return _heyApiClient.get<
    GetTeamTrendingRecommendationResponses,
    GetTeamTrendingRecommendationErrors,
    ThrowOnError
  >({
    responseType: 'json',
    security: [
      {
        scheme: 'bearer',
        type: 'http',
      },
    ],
    url: '/api/v2/clients/{person_uuid}/trending/{recommendation_uuid}',
    ...options,
  });
};

export const getExecutiveClientTrendingRecommendationActivity = <
  ThrowOnError extends boolean = false,
>(
  options: Options<
    GetExecutiveClientTrendingRecommendationActivityData,
    ThrowOnError
  >,
) => {
  return _heyApiClient.get<
    GetTeamTrendingRecommendationActivityResponses,
    GetTeamTrendingRecommendationActivityErrors,
    ThrowOnError
  >({
    responseType: 'json',
    security: [
      {
        scheme: 'bearer',
        type: 'http',
      },
    ],
    url: '/api/v2/clients/{person_uuid}/trending/{recommendation_uuid}/activity',
    ...options,
  });
};

export const postExecutiveClientTrendingRecommendationComments = <
  ThrowOnError extends boolean = false,
>(
  options: Options<
    PostExecutiveClientTrendingRecommendationCommentsData,
    ThrowOnError
  >,
) => {
  return _heyApiClient.post<
    PostTeamTrendingRecommendationCommentsResponses,
    PostTeamTrendingRecommendationCommentsErrors,
    ThrowOnError
  >({
    responseType: 'json',
    security: [
      {
        scheme: 'bearer',
        type: 'http',
      },
    ],
    url: '/api/v2/clients/{person_uuid}/trending/{recommendation_uuid}/comments',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
};

export const getExecutiveClientTrendingQueryOptions = (
  options: Options<GetExecutiveClientTrendingData>,
) => {
  return queryOptions({
    queryKey: [
      'getExecutiveClientTrending',
      options.path.person_uuid,
      options.query?.search_id,
      options,
    ] as const,
    queryFn: async ({ signal }) => {
      const { data } = await getExecutiveClientTrending({
        ...options,
        signal,
        throwOnError: true,
      });
      return data;
    },
  });
};

export const getExecutiveClientTrendingRecommendationQueryOptions = (
  options: Options<GetExecutiveClientTrendingRecommendationData>,
) => {
  return queryOptions({
    queryKey: [
      'getExecutiveClientTrendingRecommendation',
      options.path.person_uuid,
      options.path.recommendation_uuid,
      options,
    ] as const,
    queryFn: async ({ signal }) => {
      const { data } = await getExecutiveClientTrendingRecommendation({
        ...options,
        signal,
        throwOnError: true,
      });
      return data;
    },
  });
};

export const executiveClientTrendingActivityQueryKey = (args: {
  personUuid: string;
  recommendationUuid: string;
  teamUuid: string | undefined;
  teamUuids: string[] | undefined;
}) =>
  [
    'executiveClientTrendingRecommendationActivity',
    args.personUuid,
    args.recommendationUuid,
    args.teamUuid,
    args.teamUuids,
  ] as const;
